import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

function Icon({ name, ...rest }) {
  const Cmp = Icons[name] || Icons.Sparkles;
  return <Cmp {...rest} />;
}

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [filter, setFilter] = useState("Todos");

  useEffect(() => { api.get("/services").then(setServices).catch(() => setServices([])); }, []);

  const categories = useMemo(() => {
    const set = new Set(services.map((s) => s.category).filter(Boolean));
    return ["Todos", ...Array.from(set)];
  }, [services]);

  const filtered = filter === "Todos" ? services : services.filter((s) => s.category === filter);

  return (
    <div data-testid="services-page">
      <section className="pt-20 pb-12 lg:pt-32 lg:pb-16 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6 font-bold">Servicios</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-[0.95] mb-8 max-w-5xl">
            De la idea a la pantalla, <br className="hidden md:block" />
            <span className="gradient-text">de la pantalla a la comunidad.</span>
          </h1>
          <p className="text-base lg:text-lg text-muted-foreground max-w-3xl">
            14 servicios full-service para activar marcas con talentos, contenido, streaming, eventos, productos y campañas medibles.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="services-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`filter-${c}`}
                className={`px-4 py-2 rounded-sm text-sm font-semibold border transition-colors ${
                  filter === c ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-sm overflow-hidden">
            {filtered.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
                className="bg-background p-7 lg:p-9 hover:bg-secondary/40 transition-all group"
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-sm border border-border flex items-center justify-center group-hover:border-primary group-hover:text-primary transition-colors">
                    <Icon name={s.icon} className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{s.category}</span>
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight mb-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">{s.long_description || s.short_description}</p>
                <Link to="/contacto" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-primary font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                  {s.cta_label || "Quiero este servicio"} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
